import type { RelationGroupDto } from "api/types";
import { Combobox, type ComboboxOption } from "components";
import { useRelationGroups } from "hooks/useRelationGroups";
import React, { type ChangeEvent, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatRelationType } from "utilities";

import type { RelationOption, RelationTypePickerProps } from "./entityForms.types";

import styles from "./RelationTypePicker.module.css";

/** The group select's value for "no group chosen" — every relation, searchable. */
const ALL_GROUPS = "";

/**
 * Chooses a relation type in two steps: a group, then a relation within it. The relation control
 * is a searchable combobox, so a relation can also be found straight from "All groups" by typing,
 * without opening groups one by one to see where it lives.
 *
 * The groups come from the API, but they are only ever a way of arranging `options` — the relation
 * types this dialog allows. A group with nothing allowed here is not shown, and anything allowed here
 * that no group lists is still reachable under "All groups". If the groups never load, the group
 * select is left out and the picker is a single searchable list.
 */
const RelationTypePicker = ({
	controlClassName,
	disabled = false,
	onChange,
	options,
	value
}: RelationTypePickerProps) => {
	const { t } = useTranslation("homePage");
	const groups = useRelationGroups();
	const [groupKey, setGroupKey] = useState<string>(ALL_GROUPS);

	// Falls back to the spaced-out key, so a group the API adds before the locale file catches up still
	// reads as words rather than as a translation key.
	const groupLabel = useCallback(
		(key: string): string => t(`editor.relationGroups.${key}`, { defaultValue: formatRelationType(key) }),
		[t]
	);

	const visibleGroups = useMemo<RelationGroupDto[]>(() => {
		const allowed = new Set(options.map((option: RelationOption) => option.value));

		return groups
			.map((group: RelationGroupDto) => ({
				key: group.key,
				relationTypes: group.relationTypes.filter((relationType: string) => allowed.has(relationType))
			}))
			.filter((group: RelationGroupDto) => group.relationTypes.length > 0);
	}, [groups, options]);

	// The first group each relation appears in: its label under "All groups", and the group to switch to
	// when the relation is picked from there.
	const groupOfRelation = useMemo(() => {
		const byRelation = new Map<string, string>();

		visibleGroups.forEach((group: RelationGroupDto) =>
			group.relationTypes.forEach((relationType: string) => {
				if (!byRelation.has(relationType)) {
					byRelation.set(relationType, group.key);
				}
			})
		);

		return byRelation;
	}, [visibleGroups]);

	const relationOptions = useMemo<ComboboxOption[]>(() => {
		const selectedGroup = visibleGroups.find((group: RelationGroupDto) => group.key === groupKey);

		if (selectedGroup) {
			return selectedGroup.relationTypes.map((relationType: string) => ({
				label: formatRelationType(relationType),
				value: relationType
			}));
		}

		// All groups: listed in group order, each relation once, followed by anything no group covers.
		const listed = new Set<string>();
		const everything: ComboboxOption[] = [];

		visibleGroups.forEach((group: RelationGroupDto) =>
			group.relationTypes.forEach((relationType: string) => {
				if (!listed.has(relationType)) {
					listed.add(relationType);
					everything.push({
						description: groupLabel(group.key),
						label: formatRelationType(relationType),
						value: relationType
					});
				}
			})
		);

		options.forEach((option: RelationOption) => {
			if (!listed.has(option.value)) {
				everything.push({ label: formatRelationType(option.value), value: option.value });
			}
		});

		return everything;
	}, [groupKey, groupLabel, options, visibleGroups]);

	const onGroupChange = useCallback(
		(event: ChangeEvent<HTMLSelectElement>): void => {
			const nextKey = event.target.value;
			setGroupKey(nextKey);

			// Never leave a chosen relation the new group does not list. Nothing chosen yet stays
			// nothing chosen: the dialog opens on the placeholder so a relation is never recorded by
			// default, and picking a group must not quietly select one.
			const nextGroup = visibleGroups.find((group: RelationGroupDto) => group.key === nextKey);
			if (value && nextGroup && !nextGroup.relationTypes.includes(value)) {
				onChange(nextGroup.relationTypes[0]);
			}
		},
		[onChange, value, visibleGroups]
	);

	const onRelationChange = useCallback(
		(relationType: string): void => {
			// Found by searching "All groups": show which group it came from.
			if (groupKey === ALL_GROUPS) {
				const owningGroup = groupOfRelation.get(relationType);
				if (owningGroup) {
					setGroupKey(owningGroup);
				}
			}

			onChange(relationType);
		},
		[groupKey, groupOfRelation, onChange]
	);

	return (
		<div className={styles.picker}>
			{visibleGroups.length > 0 && (
				<select
					aria-label={t("editor.relationPicker.groupLabel")}
					className={controlClassName}
					disabled={disabled}
					onChange={onGroupChange}
					value={groupKey}>
					<option value={ALL_GROUPS}>{t("editor.relationPicker.allGroups")}</option>
					{visibleGroups.map((group: RelationGroupDto) => (
						<option key={group.key} value={group.key}>
							{groupLabel(group.key)}
						</option>
					))}
				</select>
			)}

			<Combobox
				ariaLabel={t("editor.relationPicker.relationLabel")}
				disabled={disabled}
				emptyText={t("editor.relationPicker.noMatches")}
				inputClassName={controlClassName}
				onChange={onRelationChange}
				options={relationOptions}
				placeholder={t("editor.relationPicker.searchPlaceholder")}
				value={value}
			/>
		</div>
	);
};

export default RelationTypePicker;
